import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Constant from '../Utils/Constant';
import BackendConnector from '../Utils/BackendConnector';
import { IoHomeOutline } from "react-icons/io5";
import { FaRegUser } from "react-icons/fa";
import { LuPlane } from "react-icons/lu";
import { RiTreeLine } from "react-icons/ri";
import { GrGallery } from "react-icons/gr";
import { FaGithub } from "react-icons/fa";
import { FaLinkedin } from "react-icons/fa";
import { FaRegEnvelope } from "react-icons/fa";
import { FaMoon, FaSun } from "react-icons/fa";

const Sidebar = ({ profilPicture, showDarkModeToggle, darkMode, setDarkMode }) => {
    const [activeSection, setActiveSection] = useState('home');
    const [showContactModal, setShowContactModal] = useState(false);
    const [contactEmail, setContactEmail] = useState("");
    const [contactMessage, setContactMessage] = useState("");
    const [contactLoading, setContactLoading] = useState(false);
    const [contactError, setContactError] = useState("");
    const [contactSuccess, setContactSuccess] = useState("");

    const menuItems = [
        { id: 'home', label: 'Accueil', icon: <IoHomeOutline /> },
        { id: 'about', label: Constant.ABOUT_ME, icon: <FaRegUser /> },
        { id: 'travels', label: Constant.MY_TRAVELS, icon: <LuPlane /> },
        { id: 'treks', label: Constant.MY_TREKS, icon: <RiTreeLine /> },
        { id: 'gallery', label: Constant.GALLERY, icon: <GrGallery /> },
    ];

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setContactLoading(true);
        setContactError("");
        setContactSuccess("");
        try {
            const response = await BackendConnector.postData('notifications/', {
                email: contactEmail,
                message: contactMessage
            });
            if (response && (response.success === undefined || response.success)) {
                setContactSuccess("Message envoyé avec succès !");
                setContactEmail("");
                setContactMessage("");
            } else {
                throw new Error("Erreur lors de l'envoi du message.");
            }
        } catch (err) {
            setContactError(err.message || "Erreur inconnue");
        } finally {
            setContactLoading(false);
        }
    };

    const handleNavClick = (sectionId, e) => {
        e.preventDefault();
        setActiveSection(sectionId);
        
        const element = document.getElementById(sectionId);
        if (element) {
            setTimeout(() => {
                element.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                });
            }, 10);
        } else {
            console.warn(`Element with id "${sectionId}" not found`);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const sections = menuItems.map(item => document.getElementById(item.id));
            const scrollPosition = window.scrollY + 100;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (section && section.offsetTop <= scrollPosition) {
                    setActiveSection(menuItems[i].id);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [menuItems]);

    return (
        <>
            {/* Modale de contact via React Portal pour être devant tout */}
            {showContactModal && createPortal(
                <div className="contact-modal-overlay" style={{zIndex: 3000, position: 'fixed'}} onClick={() => setShowContactModal(false)}>
                    <div
                        className={"contact-modal responsive-contact-modal"}
                        onClick={e => e.stopPropagation()}
                        style={{ minWidth: 420, maxWidth: 540, width: '100%', position: 'relative' }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5em',
                            background: 'linear-gradient(135deg, rgb(4, 11, 19) 0%, rgb(6, 16, 28) 100%)',
                            color: '#fff',
                            borderRadius: '10px 10px 0 0',
                            padding: '1.2em 1.5em 1em 1.5em',
                            margin: '-2rem -2.5rem 1.5rem -2.5rem',
                            boxShadow: '0 2px 8px rgba(4,11,19,0.08)'
                        }}>
                            <FaRegEnvelope size={24} style={{flexShrink: 0}} />
                            <h2 style={{margin: 0, fontWeight: 600, fontSize: '1.3rem', letterSpacing: '0.5px'}}>Contactez-moi</h2>
                            <span className="close-modal" onClick={() => setShowContactModal(false)} style={{marginLeft: 'auto', color: '#fff'}}>&times;</span>
                        </div>
                        <div style={{position: 'relative'}}>
                            {contactLoading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 10,
                                    pointerEvents: 'auto',
                                    background: 'rgba(255,255,255,0.7)',
                                    borderRadius: 8
                                }}>
                                    <div className="spinner-border text-primary" role="status" style={{width: '2.5em', height: '2.5em', borderWidth: '0.22em', background: 'transparent'}}>
                                        <span className="visually-hidden">{Constant.CONTACT_FORM_SENDING}</span>
                                    </div>
                                </div>
                            )}
                            <form className="contact-form" onSubmit={handleContactSubmit} style={{
                                background: 'rgba(72,155,216,0.04)',
                                borderRadius: 8,
                                padding: '1.2em 0.5em 0.5em 0.5em',
                                opacity: contactLoading ? 0.5 : 1,
                                pointerEvents: contactLoading ? 'none' : 'auto',
                                filter: contactLoading ? 'blur(0.5px)' : 'none',
                                transition: 'opacity 0.2s, filter 0.2s'
                            }}>
                                <label htmlFor="contact-email">Votre email</label>
                                <input
                                    id="contact-email"
                                    type="email"
                                    value={contactEmail}
                                    onChange={e => setContactEmail(e.target.value)}
                                    required
                                    placeholder="Votre email"
                                    autoComplete="email"
                                    style={{borderColor: 'rgb(4, 11, 19)', background: '#f8f9fa'}}
                                    disabled={contactLoading}
                                />
                                <label htmlFor="contact-message">{Constant.CONTACT_FORM_MESSAGE_LABEL}</label>
                                <textarea
                                    id="contact-message"
                                    value={contactMessage}
                                    onChange={e => setContactMessage(e.target.value)}
                                    required
                                    placeholder="Votre message"
                                    rows={5}
                                    style={{borderColor: 'rgb(4, 11, 19)', background: '#f8f9fa'}}
                                    disabled={contactLoading}
                                />
                                <button
                                    type="submit"
                                    disabled={contactLoading}
                                    style={{
                                        marginTop: '1em',
                                        boxShadow: '0 2px 8px rgba(4,11,19,0.08)',
                                        outline: 'none',
                                        background: 'linear-gradient(135deg, rgb(4, 11, 19) 0%, rgb(6, 16, 28) 100%)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '0.7em 1.5em',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        cursor: contactLoading ? 'not-allowed' : 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background = 'linear-gradient(135deg, rgb(6, 16, 28) 0%, rgb(4, 11, 19) 100%)'}
                                    onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(135deg, rgb(4, 11, 19) 0%, rgb(6, 16, 28) 100%)'}
                                >
                                    {Constant.CONTACT_FORM_SUBMIT}
                                </button>
                                {contactError && <div className="contact-error" style={{color: '#e53e3e', marginTop: '0.5em', textAlign: 'center'}}>{contactError}</div>}
                                {contactSuccess && <div className="contact-success" style={{color: '#38a169', marginTop: '0.5em', textAlign: 'center'}}>{contactSuccess}</div>}
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Sidebar */}
            <div className="sidebar-header">
                <div className="profile-section">
                    <div className="profile-image">
                        <img 
                            src={profilPicture ? profilPicture : Constant.PROFIL_PICTURE} 
                            alt="" 
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '50%',
                                cursor: 'pointer'
                            }}
                        />
                    </div>
                    <h3 className="profile-name">{Constant.NAME}</h3>
                    <p className="profile-title">{Constant.TITLE} <br />{Constant.AT_MONACO}</p>
                </div>
            </div>
            
            <nav className="sidebar-nav">
                <ul className="nav-list">
                    {menuItems.map((item) => (
                        <li key={item.id} className={`nav-item ${activeSection === item.id ? 'active' : ''}`}>
                            <a 
                                href={`#${item.id}`} 
                                className="nav-link"
                                onClick={(e) => handleNavClick(item.id, e)}
                            >
                                <i className="nav-icon">{item.icon}</i>
                                <span>{item.label}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>
            

            <div className="sidebar-footer">
                <div className="social-links">
                    <a href={Constant.LINKEDIN_URL} className="social-link" title="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <i><FaLinkedin /></i>
                    </a>
                    <a href={Constant.GITHUB_URL} className="social-link" title="GitHub" target="_blank" rel="noopener noreferrer">
                        <i><FaGithub /></i>
                    </a>
                    <button
                        className="social-link"
                        title="Contact"
                        style={{padding: 0, cursor: 'pointer'}}
                        onClick={() => setShowContactModal(true)}
                    >
                        <i><FaRegEnvelope /></i>
                    </button>
                        {showDarkModeToggle && typeof setDarkMode === 'function' && (
                            <button
                                className="social-link"
                                title={darkMode ? 'Désactiver le mode sombre' : 'Activer le mode sombre'}
                                style={{padding: 0, cursor: 'pointer'}}
                                onClick={() => setDarkMode(dm => !dm)}
                            >
                                <i>{darkMode ? <FaSun size={20} /> : <FaMoon size={20} />}</i>
                            </button>
                        )}
                </div>
                {/* Version injectée au build de prod depuis version.txt (absente en dev) */}
                {process.env.REACT_APP_VERSION && (
                    <p className="app-version">v{process.env.REACT_APP_VERSION}</p>
                )}
            </div>
        </>
    );
};

export default Sidebar;